import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "@/context/TranslationContext";
import ThemeToggle from "@/components/base/ThemeToggle";
import LangToggle from "@/components/base/LangToggle";

const navLinks = [
  { key: "home", href: "/" },
  { key: "services", href: "/services" },
  { key: "portfolio", href: "/portfolio" },
  { key: "projects", href: "/projects" },
  { key: "about", href: "/about" },
  { key: "pricing", href: "/pricing" },
  { key: "contact", href: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isActive = (href: string) => {
    if (href.startsWith("/#")) return false;
    return location.pathname === href || location.pathname.startsWith(href + "/");
  };

  const dashboardHref = user?.role === "admin" || user?.role === "staff" ? "/admin" : "/dashboard";
  const initials = user?.name?.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) || "U";

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-deep-black/96 backdrop-blur-xl border-b border-dark-border"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 h-[68px] flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 cursor-pointer flex-shrink-0">
            <img
              src="https://static.readdy.ai/image/8f57b6c24a127be3ec5c87bfaa3e1cdb/10eec45dd18ea3b56875e869c153a69b.png"
              alt="Schemackozo Logo"
              className="h-16 w-auto"
            />
            <span className="font-black text-white text-base tracking-widest hidden sm:block" style={{ fontFamily: "'Space Grotesk', sans-serif", letterSpacing: "0.12em" }}>
              SCHEMACKOZO
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.key}
                to={link.href}
                className={`relative px-4 py-2 text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap rounded-lg group ${
                  isActive(link.href)
                    ? "text-electric-blue"
                    : "text-silver hover:text-white"
                }`}
              >
                {t(`nav.${link.key}`)}
                {/* Active underline */}
                <span
                  className={`absolute bottom-0 left-4 right-4 h-px bg-electric-blue transition-all duration-300 ${
                    isActive(link.href) ? "opacity-100" : "opacity-0 group-hover:opacity-40"
                  }`}
                />
              </Link>
            ))}
          </div>

{/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3 flex-shrink-0">
            <LangToggle />
            <ThemeToggle />
            {isAuthenticated ? (
              <Link
                to={dashboardHref}
                className="flex items-center gap-2 bg-electric-blue text-deep-black text-sm font-bold px-5 py-2 rounded-full hover:shadow-glow-blue transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center rounded-full bg-deep-black/20 text-xs font-black">
                  {initials}
                </div>
                {user?.role === "admin" || user?.role === "staff" ? t("nav.admin_panel") : t("nav.dashboard")}
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-silver hover:text-white transition-colors text-sm font-medium px-4 py-2 whitespace-nowrap cursor-pointer"
                >
                  {t("nav.signin")}
                </Link>
                <Link
                  to="/pricing"
                  className="flex items-center gap-1.5 bg-electric-blue text-deep-black text-sm font-bold px-5 py-2.5 rounded-full hover:shadow-glow-blue transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  {t("nav.get_started")}
                  <i className="ri-arrow-right-line text-xs" />
                </Link>
              </>
            )}
          </div>

          {/* Mobile controls */}
          <div className="flex items-center gap-2 md:hidden">
            <LangToggle />
            <ThemeToggle />
            <button
              className="w-10 h-10 flex items-center justify-center text-silver hover:text-white cursor-pointer"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              <i className={`ri-${mobileOpen ? "close" : "menu-3"}-line text-xl`} />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Drawer */}
      <div
        className={`fixed inset-0 z-40 md:hidden transition-all duration-300 ${
          mobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
        <div
          className={`absolute top-0 right-0 h-full w-72 bg-dark-panel border-l border-dark-border transition-transform duration-300 ${
            mobileOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="p-6 pt-20 flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.key}
                to={link.href}
                className={`px-4 py-3 rounded-xl text-base font-medium cursor-pointer whitespace-nowrap transition-colors ${
                  isActive(link.href)
                    ? "text-electric-blue bg-electric-blue/5 border border-electric-blue/20"
                    : "text-silver hover:text-white hover:bg-dark-card"
                }`}
              >
                {t(`nav.${link.key}`)}
              </Link>
            ))}
            <div className="border-t border-dark-border mt-4 pt-4 flex flex-col gap-3">
              {isAuthenticated ? (
                <Link
                  to={dashboardHref}
                  className="text-center bg-electric-blue text-deep-black font-bold py-3 rounded-full whitespace-nowrap cursor-pointer"
                >
                  {user?.role === "admin" || user?.role === "staff" ? t("nav.admin_panel") : t("nav.dashboard")}
                </Link>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="text-center text-silver border border-dark-border rounded-xl py-2.5 hover:border-electric-blue/40 hover:text-white whitespace-nowrap cursor-pointer text-sm"
                  >
                    {t("nav.signin")}
                  </Link>
                  <Link
                    to="/pricing"
                    className="text-center bg-electric-blue text-deep-black font-bold py-3 rounded-full whitespace-nowrap cursor-pointer"
                  >
                    {t("nav.get_started")}
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
